import styled from '@emotion/styled';

import BaseLink from '@/components/common/BaseLink/BaseLink';
import { screen } from '@/components/common/breakpoints';
import { IconOnlyButton } from '@/components/common/Buttons';
import { zIndex } from '@/components/common/z-index';

export interface BannerType {
  enabled?: boolean;
  copy?: string;
  url?: string;
  closable?: boolean;
}

export interface BannerProps {
  banner?: BannerType;
  collapse?: boolean;
  onClose?: () => void;
}

export const Banner = ({ banner, collapse, onClose }: BannerProps) => {
  const { copy, url, closable } = banner;

  return (
    <StyledBanner collapse={collapse}>
      <BannerInnerWrapper>
        <LinkWrapper>
          <BannerLink
            href={url}
            aria-label={copy}
            trackingTag={{
              blockName: 'simple-link-banner',
              blockAction: 'link',
            }}
            backgroundColour="--surface-inverse-subtle"
          >
            {copy}
          </BannerLink>
        </LinkWrapper>
        {closable && (
          <CrossWrapper>
            <IconOnlyButton
              icon="Close"
              componentTheme="dark"
              onClick={onClose}
              className="tracking-simple-link-banner__close-button"
              aria-label="Close Banner"
            />
          </CrossWrapper>
        )}
      </BannerInnerWrapper>
    </StyledBanner>
  );
};

export const StyledBanner = styled.div<{ collapse: boolean }>`
  flex: 0 0 auto;
  position: relative;
  display: flex;
  text-align: center;
  justify-content: center;
  background-color: var(--surface-inverse-subtle);
  font-weight: bold;
  font-size: 16px;
  z-index: ${zIndex.banner};

  overflow: hidden;
  transition: ${({ collapse }) =>
    collapse
      ? 'max-height 0.5s cubic-bezier(0, 1, 0, 1)'
      : 'max-height 1s ease-in-out'};
  will-change: max-height;
  max-height: ${({ collapse }) => (collapse ? '0' : '1000px')};
`;

const BannerInnerWrapper = styled.div`
  padding: var(--spacing-300);
  height: var(--banner-height);
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${screen.md} {
    padding: var(--spacing-300);
  }
`;

const CrossWrapper = styled.div`
  scale: 0.75;
`;

const LinkWrapper = styled.div`
  width: 100%;
  text-align: center;
  padding: 0 var(--spacing-xx-small);
`;

const BannerLink = styled(BaseLink)`
  text-underline-offset: var(--spacing-50);
  text-decoration: underline;
`;
